import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { deleteMachine, publishMachine, unpublishMachine, updateMachine } from "../httpService";
import Button from "./button.jsx";
import Card from "./card.jsx";
import CategoryDropdown from "./categoryDropdown.jsx";
import ImagesEditor from "./imagesEditor";
import Input from "./input";
import MachineTranslationEditor from "./machineTranslationEditor.jsx";
import MarkdownDisplay from "./markdownDisplay";
import MarkdownEditor from "./markdownEditor.jsx";
import MarkdownImageInsertButton from "./markdownImageInsertButton.jsx";

const ADMIN_TABS = [
  { id: "basic", label: "Grunduppgifter" },
  { id: "media", label: "Media" },
  { id: "details", label: "Detaljer & Tillval" },
  { id: "description", label: "Extra sektion" },
  { id: "translations", label: "Översättningar" },
];

export default function AdminPanel({
  machine,
  updatedMachine,
  setUpdatedMachine,
  reinitialize,
  navigate,
  localePath,
  uploadImageFn,
  uploadVideoFn,
  handleAttributeChange,
  addAttribute,
  removeAttribute,
  addDetail,
  removeDetail,
  updateDetail,
  addExtra,
  removeExtra,
  updateExtra,
}) {
  const [tab, setTab] = useState("basic");
  const [saving, setSaving] = useState(false);
  const videoUploadRef = useRef(null);
  const descriptionRef = useRef(null);

  function handleSave() {
    setSaving(true);
    updateMachine({ id: machine.id, machine: updatedMachine })
      .then(() => {
        reinitialize();
        alert("Maskin uppdaterad");
      })
      .catch(() => alert("Kunde inte uppdatera maskin"))
      .finally(() => setSaving(false));
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 mt-4" data-testid="admin-panel">
      {/* Top bar — status + actions */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 bg-surface border border-border-default rounded-lg p-4">
        <div className="flex items-center gap-3">
          <span
            className={`inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1 rounded-full ${machine.published ? "bg-success-soft text-success-strong" : "bg-warning-soft text-warning-strong"}`}
          >
            <span
              className={`w-2 h-2 rounded-full ${machine.published ? "bg-success" : "bg-warning"}`}
            />
            {machine.published ? "Publicerad" : "Avpublicerad"}
          </span>
          <span className="text-text-muted text-sm">#{machine.id}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            target="_blank"
            rel="noopener noreferrer"
            to={localePath(`/machines/${machine.id}/quotation/create`)}
          >
            <button
              type="button"
              className="text-sm px-4 py-2 rounded border border-border-default bg-surface hover:bg-surface-soft transition-colors"
            >
              Skapa offert
            </button>
          </Link>
          <button
            type="button"
            className="text-sm px-4 py-2 rounded border border-border-default bg-surface hover:bg-surface-soft transition-colors"
            onClick={() => {
              const action = machine.published ? "avpublicera" : "publicera";
              if (window.confirm(`Är du säker på att du vill ${action} den här maskinen?`)) {
                const fn = machine.published ? unpublishMachine : publishMachine;
                fn({ id: machine.id })
                  .then(reinitialize)
                  .catch(() => alert(`Kunde inte ${action} maskinen`));
              }
            }}
          >
            {machine.published ? "Avpublicera" : "Publicera"}
          </button>
          <button
            type="button"
            className="text-sm px-4 py-2 rounded border border-error text-error hover:bg-error-soft transition-colors"
            onClick={() => {
              if (window.confirm("Är du säker på att du vill ta bort den här maskinen?")) {
                deleteMachine({ id: machine.id })
                  .then(() => navigate(localePath("/")))
                  .catch(() => alert("Kunde inte ta bort maskinen"));
              }
            }}
          >
            Ta bort
          </button>
        </div>
      </div>

      {/* Tab navigation */}
      <div
        className="flex border-b border-border-default mb-6 overflow-x-auto"
        data-testid="admin-tabs"
      >
        {ADMIN_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors -mb-px ${tab === t.id ? "border-primary text-primary" : "border-transparent text-text-muted hover:text-text-body hover:border-border-default"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="bg-surface border border-border-default rounded-lg p-6">
        {tab === "basic" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Input
              id="title"
              label="Titel"
              type="text"
              value={updatedMachine.title}
              onChange={(e) => setUpdatedMachine((p) => ({ ...p, title: e.target.value }))}
            />
            <CategoryDropdown
              value={updatedMachine.categorySlug}
              onChange={(slug) => setUpdatedMachine((p) => ({ ...p, categorySlug: slug }))}
            />
            <Input
              id="manufacturer"
              label="Tillverkare"
              type="text"
              value={updatedMachine.manufacturer}
              onChange={(e) => setUpdatedMachine((p) => ({ ...p, manufacturer: e.target.value }))}
            />
            <Input
              id="buildYear"
              label="Årsmodell"
              type="text"
              value={updatedMachine.buildYear}
              onChange={(e) => setUpdatedMachine((p) => ({ ...p, buildYear: e.target.value }))}
            />
            <Input
              id="price"
              label="Pris"
              type="number"
              value={updatedMachine.price}
              onChange={(e) => setUpdatedMachine((p) => ({ ...p, price: e.target.value }))}
            />
            <Input
              id="campaignPrice"
              label="Kampanjpris"
              type="number"
              value={updatedMachine.campaignPrice}
              onChange={(e) => setUpdatedMachine((p) => ({ ...p, campaignPrice: e.target.value }))}
            />

            <div className="sm:col-span-2">
              <h3 className="text-sm font-semibold text-text-body mb-3">
                Tekniska specifikationer
              </h3>
              {updatedMachine.attributes.map((attr, i) => (
                <div key={i} className="flex items-end gap-2 mb-3">
                  <div className="flex-1">
                    <Input
                      id={`key_${i}`}
                      label="Nyckel"
                      type="text"
                      value={attr.key}
                      onChange={(e) => handleAttributeChange(i, "key", e.target.value)}
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      id={`val_${i}`}
                      label="Värde"
                      type="text"
                      value={attr.value}
                      onChange={(e) => handleAttributeChange(i, "value", e.target.value)}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAttribute(i)}
                    className="text-error hover:text-error-strong text-sm pb-2"
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addAttribute}
                className="text-sm text-primary hover:underline"
              >
                + Lägg till specifikation
              </button>
            </div>
          </div>
        )}

        {tab === "media" && (
          <div className="space-y-8">
            <div>
              <h3 className="text-sm font-semibold text-text-body mb-3">Bilder</h3>
              <ImagesEditor
                images={updatedMachine.images}
                onChange={(imgs) => setUpdatedMachine((p) => ({ ...p, images: imgs }))}
                uploadImageFn={uploadImageFn}
              />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-text-body mb-3">Videor</h3>
              {updatedMachine.videos.map((url, i) => (
                <div key={i} className="flex items-center gap-4 mb-4">
                  {/* biome-ignore lint/a11y/useMediaCaption: admin video preview */}
                  <video src={url} className="w-40 h-28 object-cover border rounded" controls />
                  <button
                    type="button"
                    onClick={() => {
                      const v = [...updatedMachine.videos];
                      v.splice(i, 1);
                      setUpdatedMachine((p) => ({ ...p, videos: v }));
                    }}
                    className="text-error hover:text-error-strong text-sm"
                  >
                    Ta bort
                  </button>
                </div>
              ))}
              <input
                type="file"
                accept="video/*"
                style={{ display: "none" }}
                ref={videoUploadRef}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) {
                    e.target.value = "";
                    return;
                  }
                  try {
                    const u = await uploadVideoFn(file);
                    setUpdatedMachine((p) => ({ ...p, videos: [...p.videos, u] }));
                  } catch {
                    alert("Kunde inte ladda upp video");
                  } finally {
                    e.target.value = "";
                  }
                }}
              />
              <button
                type="button"
                onClick={() => videoUploadRef.current?.click()}
                className="text-sm text-primary hover:underline"
              >
                + Lägg till video
              </button>
            </div>
          </div>
        )}

        {tab === "details" && (
          <div className="space-y-8">
            <div>
              <h3 className="text-sm font-semibold text-text-body mb-3">Detaljer</h3>
              {updatedMachine.details.map((d, i) => (
                <DetailEditor
                  key={i}
                  item={d}
                  index={i}
                  label="Detalj"
                  onRemove={() => removeDetail(i)}
                  onUpdate={updateDetail}
                  uploadImageFn={uploadImageFn}
                  idPrefix="detail"
                />
              ))}
              <button
                type="button"
                onClick={addDetail}
                className="text-sm text-primary hover:underline"
              >
                + Lägg till detalj
              </button>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-text-body mb-3">Möjliga tillval</h3>
              {updatedMachine.extras.map((d, i) => (
                <DetailEditor
                  key={i}
                  item={d}
                  index={i}
                  label="Tillval"
                  onRemove={() => removeExtra(i)}
                  onUpdate={updateExtra}
                  uploadImageFn={uploadImageFn}
                  idPrefix="extra"
                />
              ))}
              <button
                type="button"
                onClick={addExtra}
                className="text-sm text-primary hover:underline"
              >
                + Lägg till tillval
              </button>
            </div>
          </div>
        )}

        {tab === "description" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label htmlFor="admin-desc" className="block text-sm font-semibold text-text-body">
                Extra sektion (Markdown)
              </label>
              <MarkdownImageInsertButton
                textareaRef={descriptionRef}
                value={updatedMachine.description}
                onChange={(val) => setUpdatedMachine((p) => ({ ...p, description: val }))}
              />
            </div>
            <textarea
              id="admin-desc"
              ref={descriptionRef}
              rows="8"
              className="w-full p-3 border border-border-default rounded-md text-sm focus:ring-2 focus:ring-primary focus:outline-none"
              value={updatedMachine.description}
              onChange={(e) => setUpdatedMachine((p) => ({ ...p, description: e.target.value }))}
            />
            <div>
              <p className="text-sm font-semibold text-text-body mb-2">Förhandsgranskning</p>
              <Card className="p-0">
                <MarkdownDisplay data={updatedMachine.description} />
              </Card>
            </div>
          </div>
        )}

        {tab === "translations" && (
          <MachineTranslationEditor
            machineId={machine.id}
            uploadImageFn={uploadImageFn}
            reinitialize={reinitialize}
          />
        )}
      </div>

      {/* Save button (always visible except on translations tab which saves per-locale) */}
      {tab !== "translations" && (
        <div className="mt-4 flex justify-end">
          <Button size="lg" loading={saving} onClick={handleSave} data-testid="save-machine">
            Spara ändringar
          </Button>
        </div>
      )}
    </div>
  );
}

function DetailEditor({ item, index, label, onRemove, onUpdate, uploadImageFn }) {
  const imageInputRef = useRef(null);
  return (
    <div className="border border-border-default rounded-lg p-4 mb-4">
      <div className="flex justify-between items-center mb-3">
        <span className="text-sm font-medium text-text-muted">
          {label} {index + 1}
        </span>
        <button
          type="button"
          onClick={onRemove}
          className="text-error hover:text-error-strong text-sm"
        >
          Ta bort
        </button>
      </div>
      <div className="flex flex-col gap-3">
        <input
          type="text"
          placeholder="Titel"
          value={item.title}
          onChange={(e) => onUpdate(index, "title", e.target.value)}
          className="border border-border-default w-full py-2 px-3 text-sm rounded focus:ring-2 focus:ring-primary focus:outline-none"
        />
        <MarkdownEditor
          rows={3}
          placeholder="Beskrivning"
          value={item.description}
          onChange={(e) => onUpdate(index, "description", e.target.value)}
        />
        <div className="flex items-center gap-3">
          {item.image && (
            <img
              src={item.image}
              alt={`${label} ${index + 1}`}
              className="w-24 h-18 object-cover rounded border"
            />
          )}
          <input
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            ref={imageInputRef}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              try {
                const url = await uploadImageFn(file);
                onUpdate(index, "image", url);
              } catch {
                alert("Kunde inte ladda upp bild");
              } finally {
                e.target.value = "";
              }
            }}
          />
          <button
            type="button"
            onClick={() => imageInputRef.current?.click()}
            className="text-sm text-primary hover:underline"
          >
            {item.image ? "Byt bild" : "Ladda upp bild"}
          </button>
        </div>
      </div>
    </div>
  );
}
